import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Search, Percent, ChevronDown, Loader2 } from "lucide-react";
import api from "@/lib/api";

/**
 * Read-only view of the CURRENT user's per-operator commissions.
 *
 * Reachable at /my-commissions. Primary audience is the AGENT role, who
 * must be able to see what commission they earn per operator but cannot
 * edit anything (all edits happen upstream via their Master Agent). Master
 * Agents can hit the same page to review what they receive from admin.
 */
export default function MyCommissions() {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [country, setCountry] = useState("");
  const [onlyAssigned, setOnlyAssigned] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get("/commissions/operators/my", {
      params: { country: country || undefined, search: q || undefined },
    })
      .then((r) => setRows(r.data.items || []))
      .finally(() => setLoading(false));
  }, [q, country]);

  const countries = useMemo(() => {
    const s = new Set();
    for (const r of rows) if (r.country_code) s.add(r.country_code);
    return [...s].sort();
  }, [rows]);

  const displayed = useMemo(() => {
    return onlyAssigned ? rows.filter((r) => r.pct > 0) : rows;
  }, [rows, onlyAssigned]);

  const assignedCount = rows.filter((r) => r.pct > 0).length;

  return (
    <div className="space-y-6" data-testid="my-commissions-page">
      <div>
        <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">
          {t("nav.my_commissions")}
        </div>
        <h1
          className="text-3xl sm:text-4xl font-bold text-slate-900"
          style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
        >
          {t("my_commissions.title")}
        </h1>
        <p className="text-slate-500 mt-1 max-w-2xl">
          {t("my_commissions.subtitle")}
        </p>
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <SummaryCard
          testid="mc-total-operators"
          label={t("my_commissions.operators")}
          value={rows.length.toLocaleString()}
        />
        <SummaryCard
          testid="mc-assigned"
          label={t("my_commissions.with_commission")}
          value={assignedCount.toLocaleString()}
          tone="emerald"
        />
        <SummaryCard
          testid="mc-unassigned"
          label={t("my_commissions.no_commission")}
          value={(rows.length - assignedCount).toLocaleString()}
          tone="slate"
        />
      </div>

      {/* Filters */}
      <div className="bg-white rounded-md border border-slate-200 shadow-sm p-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="mc-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("my_commissions.search_operators")}
            className="h-10 w-full rounded-lg border border-slate-300 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F62FE]"
          />
        </div>
        <div className="relative">
          <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">
            {t("my_commissions.country")}
          </label>
          <select
            data-testid="mc-country"
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            className="h-10 w-full rounded-lg border border-slate-300 pl-3 pr-8 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#0F62FE] appearance-none"
          >
            <option value="">{t("my_commissions.country_all")}</option>
            {countries.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          <ChevronDown className="w-4 h-4 absolute right-3 bottom-3 text-slate-400 pointer-events-none" />
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-700 select-none cursor-pointer">
          <input
            data-testid="mc-only-assigned"
            type="checkbox"
            checked={onlyAssigned}
            onChange={(e) => setOnlyAssigned(e.target.checked)}
            className="w-4 h-4 rounded border-slate-300 text-[#0F62FE] focus:ring-[#0F62FE]"
          />
          {t("my_commissions.only_assigned")}
        </label>
      </div>

      {/* Table */}
      <div className="bg-white rounded-md border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="my-commissions-table">
            <thead>
              <tr className="bg-slate-50 text-[11px] uppercase tracking-widest text-slate-500">
                <th className="px-5 py-3 text-left w-16">{t("my_commissions.col_id")}</th>
                <th className="px-5 py-3 text-left">{t("my_commissions.col_product")}</th>
                <th className="px-5 py-3 text-left">{t("my_commissions.col_country_operator")}</th>
                <th className="px-5 py-3 text-left w-40">{t("my_commissions.col_commission")}</th>
                <th className="px-5 py-3 text-left w-32">{t("my_commissions.col_status")}</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="px-5 py-8 text-center text-slate-500">
                  <Loader2 className="w-5 h-5 animate-spin inline mr-2" />{t("common.loading")}
                </td></tr>
              ) : displayed.length === 0 ? (
                <tr><td colSpan={5} className="px-5 py-10 text-center text-slate-500">
                  {onlyAssigned
                    ? t("my_commissions.no_assigned_yet")
                    : t("my_commissions.no_match")}
                </td></tr>
              ) : displayed.map((r) => (
                <tr key={r.operator_id} data-testid={`mc-row-${r.operator_id}`} className="border-b border-slate-100">
                  <td className="px-5 py-3 text-slate-500 text-xs">{r.row_number}</td>
                  <td className="px-5 py-3 font-medium text-slate-900">
                    {r.operator_name}
                    {r.kind === "cash" && (
                      <span className="ml-2 inline-flex px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 font-semibold text-[9px] uppercase tracking-wider">
                        {t("my_commissions.cash_badge")}
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-slate-600 text-xs">
                    {r.country_code || "—"}
                  </td>
                  <td className="px-5 py-3">
                    <span
                      data-testid={`mc-pct-${r.operator_id}`}
                      className={
                        "inline-flex items-center gap-1 px-2.5 py-1 rounded-md font-semibold text-sm border " +
                        (r.pct > 0
                          ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                          : "bg-slate-50 text-slate-500 border-slate-200")
                      }
                    >
                      <Percent className="w-3 h-3" />
                      {Number(r.pct).toFixed(2)}
                    </span>
                  </td>
                  <td className="px-5 py-3">
                    <span
                      className={
                        "inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border " +
                        (r.status === "ASSIGNED"
                          ? "bg-emerald-50 text-emerald-700 border-emerald-500"
                          : "bg-slate-100 text-slate-600 border-slate-300")
                      }
                    >
                      {r.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function SummaryCard({ testid, label, value, tone = "blue" }) {
  const toneCls = {
    blue: "bg-[#0F62FE]/5 text-[#0F62FE] border-[#0F62FE]/20",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    slate: "bg-slate-50 text-slate-700 border-slate-200",
  }[tone];
  return (
    <div data-testid={testid} className={`rounded-md border p-4 ${toneCls}`}>
      <div className="text-[10px] font-semibold uppercase tracking-widest opacity-80">{label}</div>
      <div className="text-2xl font-bold mt-1" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{value}</div>
    </div>
  );
}
